import React, { useState, useEffect, useCallback } from 'react';
import { apiJson, apiPost, apiPut, apiDelete } from '../api';
import { colors, theme } from '../styles';

export default function Whitelist({ account }) {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ target_username: '', never_unfollow: 1, allow_likes: 1, allow_comments: 1 });

  const load = useCallback(async () => {
    const data = await apiJson(`/accounts/${account.id}/whitelist`);
    if (data) setEntries(data.whitelist);
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.target_username.trim()) return;
    await apiPost(`/accounts/${account.id}/whitelist`, form);
    setForm({ target_username: '', never_unfollow: 1, allow_likes: 1, allow_comments: 1 });
    load();
  };

  const toggleField = async (entry, field) => {
    await apiPut(`/whitelist/${entry.id}`, { [field]: entry[field] ? 0 : 1 });
    load();
  };

  const remove = async (id) => {
    await apiDelete(`/whitelist/${id}`);
    load();
  };

  const check = (label, key) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={!!form[key]}
        onChange={e => setForm({ ...form, [key]: e.target.checked ? 1 : 0 })}
        style={{ accentColor: colors.accent }}
      />
      <span style={{ fontSize: 12, color: colors.textMuted }}>{label}</span>
    </label>
  );

  return (
    <div>
      <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 16px' }}>
        Whitelist: @{account.username}
      </h2>

      <div style={{ ...theme.card, marginBottom: 16 }}>
        <h3 style={theme.sectionTitle}>Add Whitelisted Account</h3>
        <div style={theme.row}>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Username</label>
            <input
              style={theme.input}
              value={form.target_username}
              onChange={e => setForm({ ...form, target_username: e.target.value })}
              placeholder="username (no @)"
              onKeyDown={e => e.key === 'Enter' && add()}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {check('Never Unfollow', 'never_unfollow')}
          {check('Allow Likes', 'allow_likes')}
          {check('Allow Comments', 'allow_comments')}
        </div>
        <div style={{ marginTop: 12 }}>
          <button style={theme.btnSuccess} onClick={add}>Add to Whitelist</button>
        </div>
      </div>

      <div style={{ ...theme.card }}>
        <h3 style={theme.sectionTitle}>Whitelisted Accounts</h3>
        {entries.length === 0 ? (
          <div style={{ color: colors.textDim, fontSize: 13, textAlign: 'center', padding: 16 }}>
            No whitelisted accounts. These accounts will be protected from unfollows and can have likes/comments individually controlled.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thStyle}>Username</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Never Unfollow</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Allow Likes</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Allow Comments</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={tdStyle}>
                    <span style={{ color: '#fff', fontWeight: 500 }}>@{e.target_username}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={{
                        ...theme.btnSmall,
                        background: e.never_unfollow ? colors.green + '33' : colors.bgInput,
                        color: e.never_unfollow ? colors.green : colors.textDim,
                        border: `1px solid ${e.never_unfollow ? colors.green + '44' : colors.borderLight}`,
                      }}
                      onClick={() => toggleField(e, 'never_unfollow')}
                    >{e.never_unfollow ? 'YES' : 'NO'}</button>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={{
                        ...theme.btnSmall,
                        background: e.allow_likes ? colors.accent + '33' : colors.bgInput,
                        color: e.allow_likes ? colors.accent : colors.textDim,
                        border: `1px solid ${e.allow_likes ? colors.accent + '44' : colors.borderLight}`,
                      }}
                      onClick={() => toggleField(e, 'allow_likes')}
                    >{e.allow_likes ? 'YES' : 'NO'}</button>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={{
                        ...theme.btnSmall,
                        background: e.allow_comments ? colors.accent + '33' : colors.bgInput,
                        color: e.allow_comments ? colors.accent : colors.textDim,
                        border: `1px solid ${e.allow_comments ? colors.accent + '44' : colors.borderLight}`,
                      }}
                      onClick={() => toggleField(e, 'allow_comments')}
                    >{e.allow_comments ? 'YES' : 'NO'}</button>
                  </td>
                  <td style={tdStyle}>
                    <button
                      style={{ ...theme.btnSmall, background: colors.redDark, color: '#fff' }}
                      onClick={() => remove(e.id)}
                    >Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const thStyle = { padding: '8px', textAlign: 'left', fontSize: 11, color: colors.textDim, textTransform: 'uppercase', letterSpacing: 1 };
const tdStyle = { padding: '8px', fontSize: 13 };
